import { mapRangeWithFixedBounds } from './math/mapRange'

const EARTH_RADIUS_IN_METERS = 6371008.8

function getLatitude(position: google.maps.LatLng): number {
  return position.lat()
}

function getLongitude(position: google.maps.LatLng): number {
  return position.lng()
}

function toRadians(value: number): number {
  return (value * Math.PI) / 180
}

function toDegrees(value: number): number {
  return (value * 180) / Math.PI
}

function normalizeHeading(heading: number): number {
  return (heading + 360) % 360
}

function calculateHeading(origin: google.maps.LatLng, target: google.maps.LatLng, googleApi: typeof google): number {
  const computeHeading = googleApi.maps.geometry?.spherical?.computeHeading

  if (computeHeading) {
    return computeHeading(origin, target)
  }

  const originLatitude = toRadians(getLatitude(origin))
  const targetLatitude = toRadians(getLatitude(target))
  const longitudeDifference = toRadians(getLongitude(target) - getLongitude(origin))

  const y = Math.sin(longitudeDifference) * Math.cos(targetLatitude)
  const x =
    Math.cos(originLatitude) * Math.sin(targetLatitude) -
    Math.sin(originLatitude) * Math.cos(targetLatitude) * Math.cos(longitudeDifference)

  return normalizeHeading(toDegrees(Math.atan2(y, x)))
}

function calculateDistanceBetween(origin: google.maps.LatLng, target: google.maps.LatLng, googleApi: typeof google): number {
  const computeDistanceBetween = googleApi.maps.geometry?.spherical?.computeDistanceBetween

  if (computeDistanceBetween) {
    return computeDistanceBetween(origin, target)
  }

  const originLatitude = toRadians(getLatitude(origin))
  const targetLatitude = toRadians(getLatitude(target))
  const latitudeDifference = targetLatitude - originLatitude
  const longitudeDifference = toRadians(getLongitude(target) - getLongitude(origin))

  const haversine =
    Math.sin(latitudeDifference / 2) ** 2 +
    Math.cos(originLatitude) * Math.cos(targetLatitude) * Math.sin(longitudeDifference / 2) ** 2

  return 2 * EARTH_RADIUS_IN_METERS * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
}

function calculateOffset(
  origin: google.maps.LatLng,
  distance: number,
  heading: number,
  googleApi: typeof google,
): google.maps.LatLng {
  const computeOffset = googleApi.maps.geometry?.spherical?.computeOffset

  if (computeOffset) {
    return computeOffset(origin, distance, heading)
  }

  const angularDistance = distance / EARTH_RADIUS_IN_METERS
  const headingInRadians = toRadians(heading)
  const originLatitude = toRadians(getLatitude(origin))
  const originLongitude = toRadians(getLongitude(origin))

  const targetLatitude = Math.asin(
    Math.sin(originLatitude) * Math.cos(angularDistance) +
      Math.cos(originLatitude) * Math.sin(angularDistance) * Math.cos(headingInRadians),
  )

  const targetLongitude =
    originLongitude +
    Math.atan2(
      Math.sin(headingInRadians) * Math.sin(angularDistance) * Math.cos(originLatitude),
      Math.cos(angularDistance) - Math.sin(originLatitude) * Math.sin(targetLatitude),
    )

  return new googleApi.maps.LatLng(toDegrees(targetLatitude), toDegrees(targetLongitude))
}

interface CalculateGooglePovBetweenPositions {
  google: typeof google
  origin: google.maps.LatLng
  target: google.maps.LatLng
  maxDistance?: number
}
export function calculateGooglePovBetweenPositions({
  origin,
  target,
  maxDistance = 30,
  google,
}: CalculateGooglePovBetweenPositions): google.maps.StreetViewPov {
  const calculatedHeading = calculateHeading(origin, target, google)
  const distance = calculateDistanceBetween(origin, target, google)
  const calculatedPitch = mapRangeWithFixedBounds(distance, 0, maxDistance, -20, 10)

  return {
    heading: calculatedHeading,
    pitch: calculatedPitch,
  }
}

interface CalculateGoogleReversePositionUsingPov {
  google: typeof google
  origin: google.maps.LatLng
  pov: google.maps.StreetViewPov
  maxDistance?: number
}
interface CalculateGoogleReversePositionUsingPovResult {
  target: google.maps.LatLng
  distance: number
}
export function calculateGoogleReversePositionUsingPov({
  origin,
  pov,
  maxDistance = 30,
  google,
}: CalculateGoogleReversePositionUsingPov): CalculateGoogleReversePositionUsingPovResult {
  const heading = pov.heading || 0
  const pitch = pov.pitch || 0
  const distance = mapRangeWithFixedBounds(pitch, -20, 10, 0, maxDistance)
  const target = calculateOffset(origin, distance, heading, google)

  return {
    target,
    distance,
  }
}
